import { useMemo, useRef, useState } from "react";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "@hello-pangea/dnd";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import VerticalAlignTopIcon from "@mui/icons-material/VerticalAlignTop";
import VerticalAlignBottomIcon from "@mui/icons-material/VerticalAlignBottom";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSList from "../../system-design/DSList";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSIconButton from "../../system-design/DSIconButton";
import DSTooltip from "../../system-design/DSTooltip";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import DSTextField from "../../system-design/DSTextField";
import useGetKlydosByIds from "../../Device/hooks/useGetKlydosByIds";
import { useOnScreen } from "../../../components/hooks/useOnScreen";

const RowThumbnail = ({ loopUrl }: { loopUrl?: string }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isOnScreen = useOnScreen(containerRef);

  return (
    <div
      ref={containerRef}
      style={{
        borderRadius: "50%",
        width: 36,
        height: 36,
        flex: "0 0 36px",
        overflow: "hidden",
        backgroundColor: "rgba(0, 0, 0, 0.08)",
      }}
    >
      {isOnScreen && loopUrl && (
        <video
          muted
          src={loopUrl}
          loop
          autoPlay
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      )}
    </div>
  );
};

// Same position box as the firmware klydos table (and the greet categories
// index cell): type a position, the row moves there.
const PositionInput = ({
  position,
  total,
  onMove,
}: {
  position: number;
  total: number;
  onMove: (newPosition: number) => void;
}) => {
  const commit = (raw: string) => {
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return;
    const clamped = Math.min(Math.max(Math.round(parsed), 1), total);
    if (clamped === position) return;
    onMove(clamped);
  };

  return (
    <DSTextField
      key={`pos-${position}`}
      size="small"
      defaultValue={position}
      onBlur={(e) => commit(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
      inputProps={{ style: { padding: "2px 6px", textAlign: "center" } }}
      sx={{ width: "56px" }}
    />
  );
};

type ReorderKlydosDialogProps = {
  title: string;
  klydosIds: string[];
  onSave: (klydosIds: string[]) => void | Promise<void>;
  onClose: () => void;
  isSaving?: boolean;
  /** One line under the search box saying what this order controls. */
  description?: string;
};

/**
 * Drag-and-drop editor for the ORDER of a klydo id list (a firmware's klydos,
 * a device's favorites, ...). It only permutes the list it is given — never
 * adds or removes ids (that is the pick dialog's job).
 */
const ReorderKlydosDialog = ({
  title,
  klydosIds,
  onSave,
  onClose,
  isSaving,
  description,
}: ReorderKlydosDialogProps) => {
  const [order, setOrder] = useState<string[]>(klydosIds);
  const [search, setSearch] = useState("");

  const { klydos, isLoading } = useGetKlydosByIds(klydosIds);

  const klydoById = useMemo(
    () => new Map(klydos.map((k) => [k.id, k])),
    [klydos],
  );

  const isDirty = useMemo(
    () => order.some((id, i) => id !== klydosIds[i]),
    [order, klydosIds],
  );

  // Searching filters what's rendered, and dragging inside a filtered list
  // would reorder against the wrong indices — so drag is disabled while a
  // search is active and the move-to-edge buttons take over.
  const normalizedSearch = search.trim().toLowerCase();
  const matchesSearch = (id: string): boolean => {
    if (!normalizedSearch) return true;
    const klydo = klydoById.get(id);
    return (
      id.toLowerCase().includes(normalizedSearch) ||
      (klydo?.name?.toLowerCase().includes(normalizedSearch) ?? false) ||
      (klydo?.author?.name?.toLowerCase().includes(normalizedSearch) ?? false)
    );
  };
  const visibleIds = order.filter(matchesSearch);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;
    setOrder((prev) => {
      const next = prev.slice();
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const moveTo = (id: string, newPosition: number) => {
    setOrder((prev) => {
      const from = prev.indexOf(id);
      if (from === -1) return prev;
      const next = prev.slice();
      next.splice(from, 1);
      next.splice(newPosition - 1, 0, id);
      return next;
    });
  };

  const moveToEdge = (id: string, edge: "top" | "bottom") => {
    setOrder((prev) => {
      const rest = prev.filter((item) => item !== id);
      return edge === "top" ? [id, ...rest] : [...rest, id];
    });
  };

  const handleSave = async () => {
    await onSave(order);
  };

  const renderRow = (id: string, index: number, dragHandle?: JSX.Element) => {
    const klydo = klydoById.get(id);
    // An id the klydos API did not return — hard-deleted, or filtered out.
    // Still orderable, but the row has no name/thumbnail to show.
    const isUnknown = !isLoading && !klydo;
    return (
      <DSBox
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1,
          py: 0.5,
          fontSize: 13,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        {dragHandle ?? <DSBox sx={{ width: 24 }} />}
        <DSBox sx={{ flex: "0 0 56px" }}>
          <PositionInput
            position={index + 1}
            total={order.length}
            onMove={(newPosition) => moveTo(id, newPosition)}
          />
        </DSBox>
        <RowThumbnail loopUrl={klydo?.klydoAnimation?.xs ?? klydo?.loopUrl} />
        <DSBox
          sx={{
            flex: "1 1 30%",
            overflow: "hidden",
            ...(isUnknown && { color: "error.main" }),
          }}
          title={id}
        >
          {klydo?.name ?? id}
          {isUnknown && " (not found)"}
        </DSBox>
        <DSBox
          sx={{ flex: "0 0 25%", color: "text.secondary", overflow: "hidden" }}
          title={klydo?.creator}
        >
          {klydo?.author?.name ?? ""}
        </DSBox>
        <DSBox sx={{ display: "flex", gap: 0.5, ml: "auto" }}>
          <DSTooltip title="Move to top">
            <DSIconButton size="small" onClick={() => moveToEdge(id, "top")}>
              <VerticalAlignTopIcon sx={{ fontSize: 16 }} />
            </DSIconButton>
          </DSTooltip>
          <DSTooltip title="Move to bottom">
            <DSIconButton size="small" onClick={() => moveToEdge(id, "bottom")}>
              <VerticalAlignBottomIcon sx={{ fontSize: 16 }} />
            </DSIconButton>
          </DSTooltip>
        </DSBox>
      </DSBox>
    );
  };

  return (
    <DSDialog open={true} onClose={onClose} fullWidth maxWidth="md">
      <DSDialogTitle>
        <DSBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          <span>{title}</span>
          <DSTypography variant="caption" color="text.secondary">
            {`${order.length} klydos`}
          </DSTypography>
        </DSBox>
        <DSTextField
          fullWidth
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Find a klydo by name, author or id"
          sx={{ mt: 1 }}
        />
        <DSTypography variant="caption" color="text.secondary">
          {normalizedSearch
            ? "Drag is off while searching — use the move-to-top/bottom buttons."
            : `Drag rows to reorder.${description ? ` ${description}` : ""}`}
        </DSTypography>
      </DSDialogTitle>
      <DSDialogContent dividers style={{ height: "70vh" }}>
        {isLoading ? (
          <DSBox
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <DSCircularProgress />
          </DSBox>
        ) : normalizedSearch ? (
          <DSList sx={{ border: "none", borderRadius: 0 }}>
            {visibleIds.length === 0 && (
              <DSBox sx={{ textAlign: "center", p: 3 }}>
                <DSTypography color="text.secondary">
                  No klydo matches this search.
                </DSTypography>
              </DSBox>
            )}
            {visibleIds.map((id) => renderRow(id, order.indexOf(id)))}
          </DSList>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="firmware-klydos-order">
              {(droppableProvided) => (
                <DSList
                  ref={droppableProvided.innerRef}
                  {...droppableProvided.droppableProps}
                  sx={{ border: "none", borderRadius: 0 }}
                >
                  {order.map((id, index) => (
                    <Draggable key={id} draggableId={id} index={index}>
                      {(draggableProvided, draggableSnapshot) => (
                        <DSBox
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          sx={{
                            ...(draggableSnapshot.isDragging && {
                              bgcolor: "action.hover",
                              boxShadow: 2,
                            }),
                          }}
                        >
                          {renderRow(
                            id,
                            index,
                            <DSBox
                              {...draggableProvided.dragHandleProps}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                cursor: "grab",
                                color: "text.secondary",
                                width: 24,
                              }}
                            >
                              <DragIndicatorIcon sx={{ fontSize: 16 }} />
                            </DSBox>,
                          )}
                        </DSBox>
                      )}
                    </Draggable>
                  ))}
                  {droppableProvided.placeholder}
                </DSList>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </DSDialogContent>
      <DSDialogActions>
        <DSSecondaryButton
          onClick={() => setOrder(klydosIds)}
          disabled={!isDirty || isSaving}
        >
          Reset
        </DSSecondaryButton>
        <DSSecondaryButton onClick={onClose} disabled={isSaving}>
          Cancel
        </DSSecondaryButton>
        <DSPrimaryButton onClick={handleSave} disabled={!isDirty || isSaving}>
          {isSaving ? "Saving…" : "Save order"}
        </DSPrimaryButton>
      </DSDialogActions>
    </DSDialog>
  );
};

export default ReorderKlydosDialog;
